import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../shared/TailwindComponents/Button';
import {
  PiCheckSquareLight,
  PiSquareLight,
  PiFolderLight,
  PiSpinnerGapLight,
} from 'react-icons/pi';

interface DeleteMatterConfirmationProps {
  /** How many matters are being deleted (for plural messaging) */
  matterCount?: number;
  onConfirm: (options: { deleteTasks: boolean; deleteFilesFolders: boolean }) => Promise<void> | void;
  onCancel: () => void;
}

const DeleteMatterConfirmation: React.FC<DeleteMatterConfirmationProps> = ({
  matterCount = 1,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [deleteTasks, setDeleteTasks] = useState(false);
  const [deleteFilesFolders, setDeleteFilesFolders] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isPlural = matterCount > 1;

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm({ deleteTasks, deleteFilesFolders });
    } catch {
      setIsDeleting(false);
    }
  };

  if (isDeleting) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 gap-4">
        <div className="relative w-12 h-12">
          <PiSpinnerGapLight className="w-12 h-12 text-red-500 animate-spin" />
        </div>
        <div className="text-center space-y-1.5">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {isPlural
              ? t(`Deleting ${matterCount} matters...`)
              : t('Deleting matter...')}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('Please wait, this may take a moment.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Cascading delete options */}
      <div className="space-y-3 py-1 p-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          {t('Optionally, you can also delete the associated data:')}
        </p>

        {/* Delete tasks checkbox */}
        <label
          className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
          onClick={() => setDeleteTasks(!deleteTasks)}
        >
          <div className="mt-0.5 flex-shrink-0">
            {deleteTasks ? (
              <PiCheckSquareLight className="w-5 h-5 text-red-500" />
            ) : (
              <PiSquareLight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            )}
          </div>
          <div>
            <p className="text-sm text-gray-900 dark:text-gray-100">
              {t('Delete linked tasks')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {isPlural
                ? t('Permanently delete all tasks tied to these matters. If unchecked, the tasks will be kept but unlinked.')
                : t('Permanently delete all tasks tied to this matter. If unchecked, the tasks will be kept but unlinked.')}
            </p>
          </div>
        </label>

        {/* Delete files/folders checkbox */}
        <label
          className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
          onClick={() => setDeleteFilesFolders(!deleteFilesFolders)}
        >
          <div className="mt-0.5 flex-shrink-0">
            {deleteFilesFolders ? (
              <PiCheckSquareLight className="w-5 h-5 text-red-500" />
            ) : (
              <PiSquareLight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            )}
          </div>
          <div>
            <p className="text-sm text-gray-900 dark:text-gray-100">
              <span className="flex items-center gap-1.5">
                <PiFolderLight className="w-4 h-4" />
                {t('Delete linked files & folders')}
              </span>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {isPlural
                ? t('Remove all folders and files associated with these matters. If unchecked, they will remain in your file vault.')
                : t('Remove the folder and files associated with this matter. If unchecked, they will remain in your file vault.')}
            </p>
          </div>
        </label>
      </div>

      {/* Action buttons */}
      <div className="flex justify-end space-x-4 pt-6 mt-6 border-t border-gray-300 px-4">
        <Button variant="neutral" size="small" onClick={onCancel} disabled={isDeleting} className="w-24">
          {t('Cancel')}
        </Button>
        <Button
          variant="destructive"
          size="small"
          onClick={handleConfirm}
          disabled={isDeleting}
          className="w-24"
        >
          {t('Delete')}
        </Button>
      </div>
    </div>
  );
};

export default DeleteMatterConfirmation;
